import React, { forwardRef, Fragment, useEffect, useMemo, useRef, useState, } from 'react';
import styled from '@emotion/styled';
import { MegaMenuSkipToContent } from '@polestar/component-warehouse-react/components/mega-menu/internal/MegaMenuSkipToContent';
import { m as motion, useMotionValue, useMotionValueEvent, animate } from 'framer-motion';
import LayoutIndexes from '@polestar/component-warehouse-react/tokens/layout-indexes/LayoutIndexes';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import useResizeCallback, { ResizeCallbackTypes, } from '@polestar/component-warehouse-react/hooks/useResizeCallback';
import { setCSSCustomProp } from '@polestar/component-warehouse-react/hooks/useCSSCustomProp';
import { MegaMenuCSSCustomProperties, megaMenuZIndexes, tabletInlineSize, fadeTransitions, headerDisappearingMotionOptions, } from '@polestar/component-warehouse-react/components/mega-menu/internal/MegaMenu.config';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import { sectionContainerPaddings, sectionInnerStyling, } from '@polestar/component-warehouse-react/components/section/Section';
import { MegaMenuBackdrop } from './internal/MegaMenuBackdrop';
import { MegaMenuSecondaryNavigation } from './internal/MegaMenuSecondaryNavigation';
import { MegaMenuHeaderBar } from './internal/MegaMenuHeaderBar';
import { MegaMenuPrimaryNavigation, } from './internal/MegaMenuPrimaryNavigation';
import { useMegaMenu } from './internal/MegaMenuContext';
import { SwitchableThemeProvider } from '@polestar/component-warehouse-react/components/theme-providers/ThemeProviders';
import MediaQueries from '@polestar/component-warehouse-react/tokens/media-queries/MediaQueries';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { ScrollDirection, useContentElement, } from '@polestar/component-warehouse-react/contexts/contentElement';
import useMutableValue from '@polestar/component-warehouse-react/hooks/useMutableValue';
import DeviceTypes from '@polestar/component-warehouse-react/tokens/device-types/DeviceTypes';
import useMediaQuery from '@polestar/component-warehouse-react/hooks/useMediaQuery';
import useOnValuesChange from '@polestar/component-warehouse-react/hooks/useOnValuesChange';
import useCallbackRef from '@polestar/component-warehouse-react/hooks/useCallbackRef';
import { dividerLineStyle, disappearingHeaderTranslateStyle, sectionLayoutStyles, } from '@polestar/component-warehouse-react/components/mega-menu/internal/MegaMenu.styles';
import { MegaMenuSharedCloseButton } from '@polestar/component-warehouse-react/components/mega-menu/internal/MegaMenuSharedCloseButton';
import { css } from '@emotion/react';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
import { createCSSPropertyTransition } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import { keyboardOnlyDisableStyle } from '@polestar/component-warehouse-react/helpers/accessibility';
import { useThemeType } from '@polestar/component-warehouse-react/hooks/useTheme';
import { useHeader } from '@polestar/component-warehouse-react/components/header/HeaderContext';
import { ThemeExtraTypes, ThemeTypes } from '@polestar/component-warehouse-react/themes/Theme';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import { useModal } from '@polestar/component-warehouse-react/contexts/modal';
import { drawerHideAnimationDuration } from '@polestar/component-warehouse-react/components/drawer/Drawer';
import { OfferBarCSSCustomProperties } from '@polestar/component-warehouse-react/components/offer-bar/OfferBar';
import { ImporterBannerCSSCustomProperties } from '@polestar/component-warehouse-react/components/importer-banner-top/ImporterBannerTop';
import { PolestarAppCSSCustomProperties } from '@polestar/component-warehouse-react/components/polestar-app/PolestarApp';
import { cssVar } from '@polestar/component-warehouse-react/helpers/cssVar';
export var MegaMenuVariants;
(function (MegaMenuVariants) {
    MegaMenuVariants["static"] = "static";
    MegaMenuVariants["default"] = "default";
    MegaMenuVariants["alwaysVisible"] = "alwaysVisible";
})(MegaMenuVariants || (MegaMenuVariants = {}));
// re-export things that are in internal files but need to be public
export { MegaMenuCSSCustomProperties } from './internal/MegaMenu.config';
export { useMegaMenu } from './internal/MegaMenuContext';
const MegaMenu = forwardRef(({ logoProps, menuButtonProps, variant = MegaMenuVariants.default, navigation = [], actions, divider = false, subNavigation = [], ariaLabels = {
    // TODO: add to mobile/tablet
    openMenu: 'Open the menu',
    closeMenu: 'Close the menu',
    backToMainNavigation: 'Back to the main navigation',
    skipToMainContent: 'Skip to main content',
    backToHomepage: 'Go back to the homepage',
    openExternalLink: '(Opens in a new window)',
}, skipToContentId, id, ...props }, ref) => {
    const { setMainMenuItems, setMenuActive, flyoutActive, setFlyoutActive, setActiveFlyoutIndex, flyoutHasFocus, restoreElement, activeFlyoutIndex, menuActive, setMegaMenuVariant, } = useMegaMenu();
    /**
     * Provide the MegaMenu variant that is active available via Context
     */
    useEffect(() => {
        setMegaMenuVariant(variant);
    }, [setMegaMenuVariant, variant]);
    const x = useMotionValue(0);
    const [hideHeader, setHideHeader] = useState(false);
    useEffect(() => {
        hideHeader
            ? animate(x, 1, headerDisappearingMotionOptions)
            : animate(x, 0, headerDisappearingMotionOptions);
    }, [hideHeader, x]);
    // initial set
    useEffect(() => {
        setCSSCustomProp(MegaMenuCSSCustomProperties.hideHeaderProgress, `${x.get()}`);
    }, [x]);
    // set after changes to the x value
    useMotionValueEvent(x, 'change', (latest) => {
        setCSSCustomProp(MegaMenuCSSCustomProperties.hideHeaderProgress, `${latest}`);
    });
    /**
     * Parse data for flyout
     */
    const flyout = navigation
        .filter((item) => { var _a, _b; return !!((_b = (_a = item.links) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.length); })
        .map((item) => ({
        panelName: item.panelName,
        heading: item.heading,
        links: item.links,
        visuals: item.visuals,
    }));
    /**
     * Parse data for navigation items
     */
    let panelIndexCounter = 0;
    const navigationItems = navigation.map((item) => {
        var _a, _b;
        return ({
            ...item,
            panelIndex: !!((_b = (_a = item.links) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.length) ? panelIndexCounter++ : undefined,
        });
    });
    // Todo: discuss better ways to manage these strings
    /**
     * Set the Menu Top Offset with the sum of the OfferBar + Importer Bar
     */
    setCSSCustomProp(MegaMenuCSSCustomProperties.menuTopOffset, `calc(${cssVar(OfferBarCSSCustomProperties.blockSize, '0px')} + ${cssVar(ImporterBannerCSSCustomProperties.blockSize, '0px')})`);
    /**
     * Set the Max Safe Area Height with a calculation of the sum of Menu Top Offset + its own height
     */
    setCSSCustomProp(MegaMenuCSSCustomProperties.maxSafeAreaHeight, `calc(${cssVar(MegaMenuCSSCustomProperties.menuTopOffset, '0px')} + ${cssVar(MegaMenuCSSCustomProperties.blockSize, '0px')})`);
    const megaMenuHeightInView = `(${cssVar(MegaMenuCSSCustomProperties.blockSize, '0px')} * (1 - ${cssVar(MegaMenuCSSCustomProperties.hideHeaderProgress, '0')}))`;
    const subNavigationHeightRemainingInView = `(${cssVar(MegaMenuCSSCustomProperties.subNavigationHeight, '0px')} * ${cssVar(MegaMenuCSSCustomProperties.hideHeaderProgress, '0')})`;
    const safeAreaValue = variant === MegaMenuVariants.alwaysVisible
        ? cssVar(MegaMenuCSSCustomProperties.maxSafeAreaHeight, '')
        : variant === MegaMenuVariants.static
            ? `calc(min(${cssVar(MegaMenuCSSCustomProperties.maxSafeAreaHeight, '')} - ${cssVar(PolestarAppCSSCustomProperties.scrollY, '0px')}, ${cssVar(MegaMenuCSSCustomProperties.maxSafeAreaHeight, '')}))`
            : `calc((${cssVar(MegaMenuCSSCustomProperties.menuTopOffset, '0px')} - min(${cssVar(MegaMenuCSSCustomProperties.menuTopOffset, '0px')}, ${cssVar(PolestarAppCSSCustomProperties.scrollY, '0px')})) + ${megaMenuHeightInView} + ${subNavigationHeightRemainingInView})`;
    setCSSCustomProp(MegaMenuCSSCustomProperties.safeAreaHeight, safeAreaValue);
    setCSSCustomProp(MegaMenuCSSCustomProperties.blockSize, `calc(${cssVar(MegaMenuCSSCustomProperties.barHeight, '0px')} + ${cssVar(MegaMenuCSSCustomProperties.subNavigationHeight, '0px')})`);
    const [menuRef, menuNode] = useCallbackRef();
    const [subNavigationRef, subNavigationNode] = useCallbackRef();
    const { name: mediaQueryName } = useMediaQuery();
    const menuSize = useMotionValue({
        y: 0,
        // minimum height to start with
        height: 72,
    });
    const subNavigationSize = useMotionValue({ y: 0, height: 72 });
    const scrollDeltaThreshold = useMemo(() => ({
        [DeviceTypes.mobile]: 50,
        default: 150,
    }), []);
    const { scrollDirection, scrollY, scrollYDelta } = useContentElement();
    const scrollYReached = useMutableValue(false);
    const memoizedId = useSSRSafeId('mega-menu', id);
    const [zIndex, setZIndex] = useState(LayoutIndexes.popoverOutsidePortal);
    const containerRef = useRef(null);
    const hiddenHeader = useMutableValue(false);
    const themeType = useThemeType();
    const { headerTheme, headerVisible } = useHeader();
    useEffect(() => scrollYDelta.on('change', (latestScrollYDelta) => {
        // determine when to change the visibility of the MegaMenu by hiding the MegaMenuHeaderBar to show real-estate (pixels)
        if (latestScrollYDelta >
            (scrollDeltaThreshold[mediaQueryName] || scrollDeltaThreshold.default)) {
            scrollYReached.set(true);
        }
    }), [scrollYDelta, scrollYReached, mediaQueryName, scrollDeltaThreshold]);
    useEffect(() => scrollDirection.on('change', () => scrollYReached.set(false)), [scrollDirection, scrollYDelta, scrollYReached]);
    // TODO: useCallback better?
    useEffect(() => {
        // set initial dimensions
        if (subNavigationNode) {
            const { y: subNavY, height: subNavHeight } = subNavigationNode.getBoundingClientRect();
            subNavigationSize.set({ y: subNavY, height: subNavHeight });
        }
    }, [subNavigationNode, subNavigationSize]);
    const handleValueChange = (latestScrollDirection, latestScrollY, latestScrollYReached) => {
        if (variant !== MegaMenuVariants.default)
            return;
        if (latestScrollYReached) {
            const { y: subNavY, height: subNavHeight } = subNavigationSize.get();
            if (
            // only hide the header whenever the scrollY is enough to not show the empty space behind the header
            latestScrollY > subNavY + subNavHeight &&
                latestScrollDirection === ScrollDirection.down) {
                hiddenHeader.set(true);
                setCSSCustomProp(MegaMenuCSSCustomProperties.hideHeader, `1`);
                headerVisible.set(false);
                setHideHeader(true);
            }
            else {
                hiddenHeader.set(false);
                setCSSCustomProp(MegaMenuCSSCustomProperties.hideHeader, `0`);
                headerVisible.set(true);
                setHideHeader(false);
            }
        }
        else if (latestScrollY === 0) {
            // for example when anchors reference element(-id) that is on top of the page like the PDP pages
            hiddenHeader.set(false);
            setCSSCustomProp(MegaMenuCSSCustomProperties.hideHeader, `0`);
            setHideHeader(false);
        }
    };
    useOnValuesChange([scrollDirection, scrollY, scrollYReached], handleValueChange);
    useEffect(() => {
        const handleKeyPress = (event) => {
            if (event.key === 'Escape') {
                setMenuActive(false);
                setFlyoutActive(false);
                setActiveFlyoutIndex(null);
                if (flyoutHasFocus) {
                    restoreElement && restoreElement.focus();
                }
            }
        };
        if (activeFlyoutIndex !== null || menuActive) {
            document.addEventListener('keydown', handleKeyPress);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [
        setMenuActive,
        setFlyoutActive,
        setActiveFlyoutIndex,
        restoreElement,
        activeFlyoutIndex,
        flyoutHasFocus,
        menuActive,
    ]);
    // ensure that transition is not moving container from left to initial position on mobile (right + hidden)
    const resizeTimeout = useRef(null);
    const resizeDelay = useMotionValue('none');
    resizeDelay.on('change', (val) => {
        switch (val) {
            case 'resizing':
                menuNode === null || menuNode === void 0 ? void 0 : menuNode.setAttribute('data-resizing', 'true');
                if (resizeTimeout.current) {
                    clearTimeout(resizeTimeout.current);
                }
                resizeTimeout.current = setTimeout(() => {
                    resizeDelay.set('none');
                    menuNode === null || menuNode === void 0 ? void 0 : menuNode.setAttribute('data-resizing', 'false');
                }, 1000);
                break;
        }
    });
    const resizeRef = useResizeCallback((node) => {
        if (node) {
            const { y, height } = node.getBoundingClientRect();
            resizeDelay.set('resizing');
            // collect measurements to determine where to start / stop animations
            menuSize.set({ y, height });
            if (subNavigationNode) {
                const { y: subNavY, height: subNavHeight } = subNavigationNode.getBoundingClientRect();
                subNavigationSize.set({ y: subNavY, height: subNavHeight });
            }
        }
    }, [menuSize, resizeDelay, subNavigationNode, subNavigationSize], ResizeCallbackTypes.both);
    const combinedRef = useCombinedRef(ref, resizeRef);
    const subNavigationContent = useMemo(() => subNavigation.map(({ id, node, bleed }) => {
        if (!bleed) {
            return (React.createElement(HeaderSectionContainer, { key: id },
                React.createElement(HeaderSectionWrapper, null, node)));
        }
        return React.createElement(Fragment, { key: id }, node);
    }), [subNavigation]);
    useEffect(() => {
        if (menuNode) {
            setMainMenuItems(Array.from(menuNode.querySelectorAll('[data-type="chevron"], [data-type="link"], [data-type="icon-marker"]')));
        }
    }, [setMainMenuItems, menuNode]);
    /* todo: animate offset value when variant is sticky between 0 and 1 when the bar leaves the screen  */
    /* todo: when scrollOffset on the page is larger we need to scrolljack the navigation by animating to 0 or 1 based on the scroll delta and direction */
    let currentType;
    switch (headerTheme) {
        case ThemeTypes.dark:
            currentType = ThemeTypes.dark;
            break;
        case ThemeTypes.light:
            currentType = ThemeTypes.light;
            break;
        case ThemeExtraTypes.inverted:
            currentType = themeType === ThemeTypes.light ? ThemeTypes.dark : ThemeTypes.light;
            break;
        case ThemeExtraTypes.followTheme:
            currentType = themeType === ThemeTypes.light ? ThemeTypes.light : ThemeTypes.dark;
            break;
        default:
            currentType = themeType;
            break;
    }
    // ensure that the setup which works good for maintenance (no duplicate content (for SEO)), hydration is not overwriting modals like the Drawer / Dialog / SupportDialog who are in a Portal which has z-index=LayoutIndexes.popover
    const modalChangeTimeout = useRef(null);
    const checkCurrentOpenModals = useRef(0);
    const isModalOpen = useRef(false);
    const previousOpenModal = useRef({
        amount: 0,
        isExternalModal: false,
    });
    isModalOpen.current = !!flyoutActive || !!menuActive;
    const { queryMatches } = useMediaQuery();
    const handleModalChange = (openModals, { isCustomModalOpen, 
    // prevent that the open argument is used
    open: modalOpen, }) => {
        checkCurrentOpenModals.current = openModals.length;
        const timeoutDuration = drawerHideAnimationDuration * 1000 * 0.8;
        if (openModals.length > 1 && modalOpen) {
            // another modal is opened so close directly
            setZIndex(LayoutIndexes.header);
            setMenuActive(false);
            setFlyoutActive(false);
            setActiveFlyoutIndex(null);
        }
        else {
            const resetZIndexAfterTimeout = () => {
                setZIndex(checkCurrentOpenModals.current > 0 && !isModalOpen.current
                    ? LayoutIndexes.header
                    : LayoutIndexes.popoverOutsidePortal);
                modalChangeTimeout.current = null;
            };
            if (!isCustomModalOpen) {
                if (openModals.length > 0 && !modalOpen) {
                    // another basic modal is open so hide behind it
                    setZIndex(LayoutIndexes.header);
                }
                else if (previousOpenModal.current.amount > 0 &&
                    !previousOpenModal.current.isExternalModal &&
                    !modalOpen) {
                    // another basic modal is closed so wait until fully hidden before showing the MegaMenu on top again
                    if (modalChangeTimeout.current) {
                        clearTimeout(modalChangeTimeout.current);
                    }
                    modalChangeTimeout.current = setTimeout(resetZIndexAfterTimeout, timeoutDuration);
                }
                else if (previousOpenModal.current.amount > 0 &&
                    previousOpenModal.current.isExternalModal &&
                    !modalOpen) {
                    // another custom modal is closed so wait until fully hidden before showing the MegaMenu on top again
                    if (modalChangeTimeout.current) {
                        clearTimeout(modalChangeTimeout.current);
                    }
                    modalChangeTimeout.current = setTimeout(resetZIndexAfterTimeout, timeoutDuration);
                }
            }
            else {
                if (openModals.length > 0 && !modalOpen) {
                    // another custom modal is open so hide behind it
                    setZIndex(LayoutIndexes.header);
                }
                else if (previousOpenModal.current.amount > 0 && !modalOpen) {
                    // another custom modal is closed so wait until fully hidden before showing the MegaMenu on top again
                    if (modalChangeTimeout.current) {
                        clearTimeout(modalChangeTimeout.current);
                    }
                    modalChangeTimeout.current = setTimeout(resetZIndexAfterTimeout, timeoutDuration);
                }
            }
        }
        previousOpenModal.current = {
            amount: openModals.length,
            isExternalModal: isCustomModalOpen,
        };
    };
    useModal({
        ref: containerRef,
        open: !!flyoutActive || (!!menuActive && (queryMatches.desktop || queryMatches.desktopWide)),
        onModalsChange: handleModalChange,
        customModal: true,
    });
    const barRef = useResizeCallback((node) => {
        setCSSCustomProp(MegaMenuCSSCustomProperties.barHeight, `${node.getBoundingClientRect().height}px`);
    }, [], ResizeCallbackTypes.height);
    return (React.createElement(SwitchableThemeProvider, { type: currentType },
        React.createElement(MegaMenuSkipToContent, { id: `${memoizedId}-skip-to-content`, label: ariaLabels.skipToMainContent, href: `#${skipToContentId}`, "data-type": "mega-menu-skip-content" }),
        React.createElement(Container, { "data-type": "mega-menu", variant: variant, id: memoizedId, ...props, zIndex: zIndex, ref: containerRef },
            navigation && (React.createElement(NavigationContainer, { ref: barRef, "data-type": "navigation-container", hasDivider: !!divider },
                React.createElement(MegaMenuHeader, { "data-type": "header", ref: combinedRef },
                    React.createElement(MegaMenuHeaderContent, { "data-type": "header-content" },
                        React.createElement(MegaMenuHeaderBar, { logoProps: logoProps, menuButtonProps: menuButtonProps, megaMenuId: memoizedId, ariaLabels: {
                                backToHomepage: ariaLabels.backToHomepage,
                                openMenu: ariaLabels.openMenu,
                            } }),
                        React.createElement(MegaMenuBackdrop, { "data-type": "overlay" }),
                        React.createElement(MegaMenuPrimaryNavigation, { megaMenuId: memoizedId, navigation: navigationItems, actions: actions, ref: menuRef, "data-type": "primary-navigation", "data-resizing": "false", ariaLabels: {
                                closeMenu: ariaLabels.closeMenu,
                                openExternalLink: ariaLabels.openExternalLink,
                            } }))),
                React.createElement(MegaMenuSecondaryNavigation, { megaMenuId: memoizedId, flyout: flyout, backButtonLabel: ariaLabels.backToMainNavigation, "data-type": "secondary-navigation", ariaLabels: {
                        closeMenu: ariaLabels.closeMenu,
                        openExternalLink: ariaLabels.openExternalLink,
                    } }),
                React.createElement(MegaMenuSharedCloseButton, { closeButtonLabel: ariaLabels.closeMenu }))),
            subNavigationContent && (React.createElement(SubNavigationWrapper, { "data-type": "sub-navigation", ref: subNavigationRef }, subNavigationContent)))));
});
MegaMenu.displayName = 'MegaMenu';
export default MegaMenu;
/**
 * When the container contains an element with [data-resizing="true"] it disables all CSS transition by setting the duration to 0ms
 * This helps us prevent weird glitching and transitions between breakpoints changes that could happen during resizing.
 */
const disableCSSTransitions = css `
  &:has([data-resizing='true']) * {
    transition-duration: 0ms;
  }
`;
const Container = styled('section', shouldNotForwardProps(false, 'variant', 'zIndex'))(({ variant, zIndex }) => css `
    ${disableCSSTransitions};
    position: ${variant === MegaMenuVariants.static ? 'relative' : 'sticky'};
    inline-size: 100%;
    inset: 0 0 auto;
    z-index: ${zIndex};
    pointer-events: none;

    ${MediaQueries.untilDesktop} {
      display: flex;
      flex-direction: column;

      ${keyboardOnlyDisableStyle};
    }

    ${MediaQueries.desktop} {
      display: grid;
      grid-template: auto 1fr / 1fr;
    }
  `);
// necessary for the bleed options of the sub-navigation wrapper
const HeaderSectionContainer = styled.div `
  ${responsiveStyles(sectionContainerPaddings)};
`;
const HeaderSectionWrapper = styled.div `
  margin: 0 auto;

  ${responsiveStyles(sectionInnerStyling)};
`;
const NavigationContainer = styled(motion.div)(({ theme, hasDivider }) => css `
    z-index: ${megaMenuZIndexes.navigationContainer};

    ${MegaMenuCSSCustomProperties.isLightTheme}: ${theme.megaMenu.isLightTheme};
    ${MegaMenuCSSCustomProperties.background}: ${theme.megaMenu.background};
    ${MegaMenuCSSCustomProperties.color}: ${theme.megaMenu.color};
    ${MegaMenuCSSCustomProperties.tabletMaxInlineSize}: ${tabletInlineSize};
    ${MegaMenuCSSCustomProperties.divider}: ${hasDivider ? theme.megaMenu.divider : undefined};

    ${MediaQueries.untilDesktop} {
      width: 100%;
      display: flex;
      flex-direction: column;
    }

    ${MediaQueries.desktop} {
      position: relative;
      ${disappearingHeaderTranslateStyle};
    }
  `);
const MegaMenuHeader = styled.div `
  position: relative;
  transition: ${createCSSPropertyTransition('background-color', fadeTransitions)};
  display: flex;
  flex-direction: row;
  justify-content: center;
  overflow: hidden;

  /* ensure that the height will not change when data is loaded on the client-side and not available initially */

  ${responsiveStyles({
    minBlockSize: {
        mobile: createCustomSpacing(56 / 16),
        tablet: createCustomSpacing(64 / 16),
        desktop: createCustomSpacing(72 / 16),
    },
})}
  ${MediaQueries.desktop} {
    overflow: unset;
    background: ${cssVar(MegaMenuCSSCustomProperties.background, '')};

    ${dividerLineStyle};
  }
`;
const MegaMenuHeaderContent = styled.div `
  ${sectionLayoutStyles};
  display: grid;
  grid-template-columns: 1fr auto;

  ${MediaQueries.desktop} {
    padding-block: ${createCustomSpacing(20 / 16)};
    grid-template-columns: auto 1fr;
    gap: ${Spacings.large};
  }
`;
const SubNavigationWrapper = forwardRef((props, ref) => {
    const resizeRef = useResizeCallback((node) => {
        if (node) {
            const { height } = node.getBoundingClientRect();
            setCSSCustomProp(MegaMenuCSSCustomProperties.subNavigationHeight, `${height}px`);
        }
    }, [], ResizeCallbackTypes.height);
    const combinedRef = useCombinedRef(ref, resizeRef);
    return React.createElement(SubNavigationWrapperContainer, { ref: combinedRef, ...props });
});
/**
 * Wrapper around the subNavigation-slot.
 */
const SubNavigationWrapperContainer = styled.div `
  inline-size: 100%;
  position: relative;
  pointer-events: auto;

  ${disappearingHeaderTranslateStyle};
`;
